<template>
  <!--
    Linke Spalte „Beteiligte zuordnen“ (Kundenfeedback 24.10.2026, XIX):
    ganz oben eine Suche, die rechts die Suchergebnisse öffnet; darunter nur
    die Rollen. Besetzte Rollen sind grün (mit Häkchen und den Namen der
    Zugeordneten), unbesetzte schwarz. Klick auf eine besetzte Rolle zeigt
    rechts genau diese Beteiligten zum Bearbeiten bzw. „Weitere hinzufügen“,
    Klick auf eine unbesetzte Rolle öffnet rechts die Suche für diese Rolle.
    Treffer von rechts lassen sich weiterhin auf eine Rolle ziehen.
  -->
  <nav class="rc" :aria-label="$gt('Rollen in dieser Akte')" data-testid="apd-roles">
    <div class="rc__search">
      <Search :size="15" class="rc__search-ico" aria-hidden="true" />
      <input
        :value="query"
        class="rc__input"
        type="search"
        :placeholder="$gt('Kontakt suchen …')"
        :aria-label="$gt('Kontakt suchen')"
        data-testid="apd-left-search"
        @input="$emit('search', $event.target.value)"
        @focus="$emit('search-focus')"
      />
    </div>

    <p class="rc__title">{{ $gt('Rollen in dieser Akte') }}</p>
    <p v-if="dragActive" class="rc__hint">{{ $gt('Auf eine Rolle ziehen, um zuzuordnen') }}</p>

    <ul class="rc__list">
      <li
        v-for="group in groups"
        :key="group.key"
        class="rc__role"
        :class="{
          'rc__role--active': activeRole === group.key,
          'rc__role--filled': !group.isEmpty,
          'rc__role--drop': dropKey === group.key,
          'rc__role--droppable': dragActive,
        }"
        :data-testid="'apd-role-' + group.key"
        @dragover.prevent="onDragOver(group.key, $event)"
        @dragleave="onDragLeave(group.key)"
        @drop.prevent="onDrop(group.key)"
      >
        <button
          type="button"
          class="rc__head"
          :aria-current="activeRole === group.key ? 'true' : undefined"
          :title="group.isEmpty ? $gt('Rechts einen Kontakt für diese Rolle suchen') : $gt('Rechts die Beteiligten dieser Rolle anzeigen und bearbeiten')"
          @click="$emit('select-role', group.key)"
        >
          <span class="rc__line">
            <Check v-if="!group.isEmpty" :size="14" class="rc__check" aria-hidden="true" />
            <span class="rc__label">{{ group.label }}</span>
            <span v-if="group.isEmpty && group.priority !== 'optional'" class="rc__prio">
              {{ group.priority === 'pflicht' ? $gt('Pflicht') : $gt('Empfohlen') }}
            </span>
          </span>
          <span v-if="!group.isEmpty" class="rc__names" data-testid="apd-role-names">{{ summaryOf(group) }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
import { Check, Search } from "lucide-vue-next";
import { roleSummary } from "./participantDialogModel";

export default {
  name: "RoleColumn",

  components: { Check, Search },

  props: {
    /** Ergebnis von buildRoleColumn() */
    groups: { type: Array, default: () => [] },
    /** Rolle, die rechts gerade angezeigt bzw. gesucht wird ('' = keine) */
    activeRole: { type: String, default: "" },
    /** Rechts wird gerade ein Treffer gezogen */
    dragActive: { type: Boolean, default: false },
    /** Suchbegriff (gleichlaufend mit der Suche rechts) */
    query: { type: String, default: "" },
  },

  emits: ["select-role", "drop-role", "search", "search-focus"],

  data() {
    return { dropKey: "" };
  },

  watch: {
    dragActive(active) {
      if (!active) this.dropKey = "";
    },
  },

  methods: {
    summaryOf(group) { return roleSummary(group); },
    onDragOver(key, event) {
      if (!this.dragActive) return;
      if (event?.dataTransfer) event.dataTransfer.dropEffect = "copy";
      this.dropKey = key;
    },
    onDragLeave(key) {
      if (this.dropKey === key) this.dropKey = "";
    },
    onDrop(key) {
      this.dropKey = "";
      if (this.dragActive) this.$emit("drop-role", key);
    },
  },
};
</script>

<style scoped>
.rc {
  /* Grün nur für „besetzt“ – dunkel genug für gut lesbare Schrift auf Weiß. */
  --rc-filled: #2e7d32;
  display: flex; flex-direction: column; gap: 4px;
}

.rc__search {
  display: flex; align-items: center; gap: 8px; height: 38px; margin-bottom: 10px; padding: 0 12px;
  border: 1px solid var(--border-grey-color); border-radius: 10px; background: #fff;
  transition: border-color var(--duration-fast, 150ms) ease, box-shadow var(--duration-fast, 150ms) ease;
}
.rc__search:focus-within { border-color: var(--primary-color); box-shadow: 0 0 0 3px var(--primary-light-color-transparent); }
.rc__search-ico { flex-shrink: 0; color: var(--grey-color); }
.rc__input { flex: 1; min-width: 0; border: none; outline: none; background: transparent; font: inherit; font-size: 13px; color: var(--dark-color); }
.rc__input::placeholder { color: var(--disabled-grey-color); }

.rc__title {
  margin: 0 0 2px; padding: 0 10px;
  font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--grey-color);
}
.rc__hint { margin: 0 0 4px; padding: 0 10px; font-size: 11.5px; color: var(--primary-color); }
.rc__list { list-style: none; margin: 0; padding: 0; }

.rc__role {
  border: 1px solid transparent; border-radius: 8px;
  transition: background-color var(--duration-fast, 150ms) ease, border-color var(--duration-fast, 150ms) ease;
}
.rc__role + .rc__role { margin-top: 2px; }
.rc__role--active { background: var(--primary-light-color-transparent); }
.rc__role--droppable { border-style: dashed; border-color: var(--border-grey-color); }
.rc__role--drop { background: var(--primary-light-color-transparent); border-color: var(--primary-color); }

.rc__head {
  display: flex; flex-direction: column; align-items: stretch; gap: 1px; width: 100%;
  padding: 7px 10px; border: none; border-radius: 8px; background: none;
  font: inherit; text-align: left; cursor: pointer; color: var(--dark-color);
}
.rc__head:hover { background: var(--primary-light-color-transparent); }
.rc__head:focus-visible { outline: 2px solid var(--primary-color); outline-offset: -2px; }

/* Kundenfeedback 24.10.2026 (XIX): „EMPFOHLEN“ überlappte „Sachverständigen-
   büro“ – Bezeichnung umbricht jetzt sauber, der Hinweis steht daneben. */
.rc__line { display: flex; align-items: center; gap: 6px; min-width: 0; }
.rc__check { flex-shrink: 0; color: var(--rc-filled); }
.rc__label { flex: 1 1 auto; min-width: 0; font-size: 13px; font-weight: 500; line-height: 1.35; overflow-wrap: anywhere; }
.rc__role--filled .rc__label { font-weight: 650; color: var(--rc-filled); }
.rc__role--active .rc__label { text-decoration: underline; text-underline-offset: 3px; }
.rc__prio { flex-shrink: 0; font-size: 11px; color: var(--grey-color); white-space: nowrap; }
.rc__names {
  padding-left: 20px; font-size: 12px; line-height: 1.4; color: var(--dark-color);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .rc__role, .rc__search { transition: none; }
}
</style>
